"use client";

import { CitationTool } from "@/components/citation-tool";
import { useToolUi } from "@/components/tools/tool-ui";
import { getFeaturedStyles } from "@/data/citation-styles";

/** English strings. A translated content file overrides these through `ui`. */
export const ARTWORK_CITATION_GENERATOR_STRINGS = {
  howToCiteAnArtwork: "How to Cite an Artwork",
  n1EnterTheArtworkDetails: "1. Enter the artwork details.",
  typeTheArtistTheWork:
    "Type the artist, the work's title, the year it was created, and the medium — oil on canvas, marble, photograph. Name the museum or gallery that holds it along with its city; if you viewed the work online, paste the URL of the page instead.",
  n2PickAStyle: "2. Pick a style.",
  chooseApaMlaChicagoOr:
    "Choose APA, MLA, Chicago, or any of the {styleCount} styles. APA puts the medium in brackets after the title —",
  painting: "[Painting]",
  sculptureWhileMlaListsThe:
    ", [Sculpture] — while MLA lists the museum and city as the container after the year.",
  n3Generate: "3. Generate.",
  weBuildAFullArtwork:
    " We build a full artwork reference. Edit any field, then copy it or export to BibTeX, RIS, CSV, or Word.",
} as const;

/**
 * Live citation generator embedded on the Artwork & Painting landing page. Artworks are
 * cited as graphic works (artist, title, year, medium, museum + location — or a URL for
 * online viewing), so the tool opens on the manual-entry tab preset to the CSL `graphic`
 * type. Users pick any of the 10,834 styles — no engine change.
 */
function ArtworkGenerator() {
  return (
    <CitationTool
      initialManualType="graphic"
      initialMode="manual"
      styles={getFeaturedStyles()}
    />
  );
}

export default function ArtworkCitationGeneratorWidget() {
  const strings = useToolUi(ARTWORK_CITATION_GENERATOR_STRINGS);

  return (
    <>
      {/* Live generator (manual entry preset to the artwork / graphic source type) */}
      <ArtworkGenerator />
      {/* How to */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.howToCiteAnArtwork}
        </h2>
        <ol className="space-y-3 text-muted-foreground text-sm leading-relaxed">
          <li>
            <strong className="text-foreground">
              {strings.n1EnterTheArtworkDetails}
            </strong>{" "}
            {strings.typeTheArtistTheWork}
          </li>
          <li>
            <strong className="text-foreground">{strings.n2PickAStyle}</strong>{" "}
            {strings.chooseApaMlaChicagoOr}{" "}
            <span className="font-medium text-foreground">
              {strings.painting}
            </span>
            {strings.sculptureWhileMlaListsThe}
          </li>
          <li>
            <strong className="text-foreground">{strings.n3Generate}</strong>
            {strings.weBuildAFullArtwork}
          </li>
        </ol>
      </section>
    </>
  );
}

/**
 * English only for now. Add locales here as this page type is translated — the
 * registry in src/lib/i18n/localized-routes.ts has to agree, or the proxy will
 * redirect the URL this generates away again.
 */
export function generateStaticParams() {
  return [{ locale: "en" }];
}

export const dynamicParams = false;
